<template>
  <div class="post-downloader-page">
    <HeadNav page="instasave" />

    <HeroSection
      :title="heroTitle"
      :subtitle="heroSubtitle"
      :input-placeholder="inputPlaceholder"
      :active-tab="activeTab"
      @tab-change="switchTab"
      @download-content="downloadContent"
      @go-to-download="goToDownload"
      @join-telegram="joinTelegramChannel"
    />

    <div class="content-wrapper">

      <section class="section">
        <h2>Instagram Post Download</h2>
        <p>
          Welcome to <strong><a class="jump-url" href="https://instasaves.io/" target="_blank" rel="noopener">InstaSave</a></strong>, the fastest and most reliable tool to
          <em>download Instagram posts</em>—including videos, Reels, and images—from any public Instagram account. With Instasave, you can save content offline quickly, securely, and in high quality, whether on our web platform or mobile app.
        </p>
      </section>

      <section class="section highlight-section">
        <h2>How to Download Instagram Posts HD with InstaSave</h2>
        <div class="steps">
          <div class="step">
            <h3>Step 1: Copy the Instagram Post URL</h3>
            <p>On Instagram, tap the three dots next to the post → “Copy Link”.</p>
          </div>
          <div class="step">
            <h3>Step 2: Paste the URL in Instasave</h3>
            <p>Use the input box below to paste the copied link.</p>
          </div>
          <div class="step">
            <h3>Step 3: Click “Download”</h3>
            <p>InstaSave fetches the media and provides download links. Choose the format you want and save it directly to your device.</p>
          </div>
        </div>
      </section>

      <section class="section app-section">
        <h2>InstaSave Mobile App for Instagram Post Download</h2>
        <div class="app-card">
          <img src="@/assets/post-image.png" alt="InstaSave mobile app" />
          <div>
            <p>Take Instagram post downloads wherever you go:</p>
            <ul>
              <li><strong>Download on the Go</strong>: Save videos or photos directly on your phone.</li>
              <li><strong>Fast &amp; Lightweight</strong>: Minimal bandwidth use and optimized speed.</li>
              <li><strong>Offline Library</strong>: Manage all downloaded posts in one place.</li>
              <li><strong>Share or Repost</strong>: Share saved content or repost responsibly.</li>
            </ul>
          </div>
        </div>
      </section>

      <section class="section">
        <h2>Key Features of InstaSave App</h2>
        <div class="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Feature</th>
                <th>Benefit</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Bulk Download</td>
                <td>Save multiple posts or carousel content at once.</td>
              </tr>
              <tr>
                <td>High-Speed Processing</td>
                <td>Instant download without waiting.</td>
              </tr>
              <tr>
                <td>No Account Needed</td>
                <td>Just paste the link—no login required.</td>
              </tr>
              <tr>
                <td>Cross-Platform</td>
                <td>Works on desktop, mobile browser, and the app.</td>
              </tr>
              <tr>
                <td>Secure &amp; Private</td>
                <td>We don’t store login info or downloaded content.</td>
              </tr>
              <tr>
                <td>Ad-Free Experience</td>
                <td>Smooth interface with no annoying pop-ups.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section class="section">
        <h2>Why Choose InstaSave for Instagram Post Download HD?</h2>
        <ul class="benefits-list">
          <li><strong>Simple &amp; Intuitive</strong>: Copy the Instagram post link, paste it into Instasave, and click <strong>Download</strong>—done in seconds.</li>
          <li><strong>High-Quality Downloads</strong>: Preserve the original resolution of HD videos and crisp images.</li>
          <li><strong>Supports All Formats</strong>: Download Reels, IGTV, standard posts, and carousel posts effortlessly.</li>
          <li><strong>Fast &amp; Lightweight</strong>: No heavy software or browser extensions needed.</li>
          <li><strong>Works Everywhere</strong>: Use Instasave on desktop, mobile browser, or through our app.</li>
          <li><strong>Safe &amp; Private</strong>: No Instagram login required; your credentials stay secure.</li>
        </ul>
      </section>

      <section class="faq-section">
        <div class="faq-container">
          <h2 class="section-title">FAQs About Instagram Post Download</h2>
          <div class="faq-list">
            <div class="faq-item" v-for="(faq, index) in faqList" :key="faq.question">
              <div class="faq-question" @click="toggleFaq(index)">
                <h3>{{ faq.question }}</h3>
                <span class="faq-icon" :class="{ active: faq.isOpen }">+</span>
              </div>
              <div class="faq-answer" v-show="faq.isOpen">
                <p>{{ faq.answer }}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import HeadNav from '@/components/HeadNav.vue'
import Breadcrumb from '@/components/Breadcrumb.vue'
import HeroSection from '@/components/HeroSection.vue'

export default {
  name: 'PostDownloader',
  components: {
    HeadNav,
    Breadcrumb,
    HeroSection
  },
  data() {
    return {
      activeTab: 'post',
      heroTitle: 'InstaSave – Instagram Post Downloader',
      heroSubtitle: 'Download Instagram Post and Save Instagram Videos & Photos Instantly',
      inputPlaceholder: 'Paste Instagram post link here...',
      faqList: [
        {
          question: 'Can I download private Instagram posts?',
          answer: 'No. Only public Instagram posts are supported for download with InstaSave.',
          isOpen: false
        },
        {
          question: 'Do I need to log in to Instagram?',
          answer: 'No account login is required. Just paste a public Instagram post link to download it instantly.',
          isOpen: false
        },
        {
          question: 'What types of Instagram content can I download?',
          answer: 'You can save standard posts, HD photos, videos, Reels, IGTV, and carousel posts as long as they are public.',
          isOpen: false
        },
        {
          question: 'Is there a limit to how many posts I can download?',
          answer: 'There is no download limit. InstaSave lets you grab as many Instagram posts as you need in full quality.',
          isOpen: false
        },
        {
          question: 'Is InstaSave safe to use?',
          answer: 'Yes. We never ask for your Instagram password, and we do not store your downloaded media.',
          isOpen: false
        },
        {
          question: 'Why could a download fail?',
          answer: 'Downloads may fail if the post was deleted, set to private, or the URL was copied incorrectly. Try again with the correct public link.',
          isOpen: false
        }
      ]
    }
  },
  computed: {
    breadcrumbs() {
      return this.$route.matched.reduce((acc, route) => {
        const crumbs = route.meta.breadcrumb || []
        return acc.concat(crumbs)
      }, [])
    }
  },
  methods: {
    switchTab(tabId) {
      this.activeTab = tabId
    },
    downloadContent(link) {
      if (!link || !link.trim()) {
        return
      }
      console.log('Attempt download:', link)
    },
    goToDownload() {
      this.$router.push('/instagram-apk-downlad/')
    },
    joinTelegramChannel() {
      window.open('https://t.me/+M75kow80SlQ1MjZl', '_blank')
    },
    toggleFaq(index) {
      this.faqList[index].isOpen = !this.faqList[index].isOpen
    }
  },
  metaInfo() {
    return {
      title: 'Instasave – Instagram Post Download Made Easy',
      meta: [
        {
          name: 'description',
          content: 'Download any Instagram post in seconds with InstaSave. Fast, high-quality, and hassle-free Instagram post and video downloads for everyone.'
        },
        {
          name: 'keywords',
          content: 'Instagram Post Download, Instagram Post Download HD'
        }
      ],
      link: [
        {
          rel: 'canonical',
          href: 'https://instasaves.io/post-downloader/'
        }
      ]
    }
  }
}
</script>

<style scoped>
.post-downloader-page {
  min-height: 100vh;
  background: #f8fafc;
}

.content-wrapper {
  max-width: 960px;
  margin: 0 auto;
  padding: 2rem 1.25rem 4rem;
}

h1,
h2,
h3 {
  font-weight: 700;
  color: #1f2937;
  line-height: 1.2;
}

h1 {
  font-size: 2.5rem;
  margin-bottom: 0.75rem;
}

.hero-subtitle {
  font-size: 1.125rem;
  color: #4b5563;
  margin-bottom: 1.5rem;
}

.paste-link-card {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: center;
  background: #ffffff;
  border-radius: 999px;
  padding: 0.5rem;
  box-shadow: 0 15px 35px rgba(15, 23, 42, 0.12);
}

.paste-link-card input {
  flex: 1 1 300px;
  border: none;
  padding: 0.75rem 1rem;
  border-radius: 999px;
  font-size: 1rem;
  background: transparent;
  outline: none;
}

.paste-link-card button {
  border: none;
  border-radius: 999px;
  padding: 0.75rem 1.5rem;
  background: #2563eb;
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;
}

.paste-link-card button:hover {
  background: #1e40af;
}

.section {
  background: #fff;
  border-radius: 1rem;
  padding: 2rem;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
  margin-bottom: 2rem;
}

.section p {
  color: #4b5563;
  line-height: 1.7;
  margin-top: 1rem;
}

.section ul {
  margin: 1rem 0 0;
  padding-left: 1.25rem;
  color: #4b5563;
  line-height: 1.6;
}

.section-title {
  font-size: 2rem;
  font-weight: 700;
  color: #1f2937;
  text-align: center;
  margin-bottom: 2rem;
}

.highlight-section {
  background: #eef2ff;
}

.steps {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem;
  margin-top: 1.5rem;
}

.step {
  background: #fff;
  border-radius: 0.75rem;
  padding: 1.25rem;
  box-shadow: 0 12px 30px rgba(79, 70, 229, 0.15);
}

.app-section .app-card {
  display: grid;
  gap: 1.5rem;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  align-items: center;
}

.app-card img {
  width: 100%;
  border-radius: 1rem;
}

.table-wrapper {
  overflow-x: auto;
  margin-top: 1.5rem;
}

table {
  width: 100%;
  border-collapse: collapse;
  text-align: left;
}

th,
td {
  padding: 0.875rem;
  border-bottom: 1px solid #e5e7eb;
  color: #1f2937;
}

th {
  background: #f3f4f6;
  font-size: 0.95rem;
}

.benefits-list {
  list-style: disc;
}

.faq-section {
  padding: 80px 0;
  background: #f8fafc;
}

.faq-container {
  max-width: 900px;
  margin: 0 auto;
  padding: 0 1.5rem;
}

.faq-list {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.faq-item {
  background: #fff;
  border-radius: 1rem;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.faq-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 15px 35px rgba(15, 23, 42, 0.12);
}

.faq-question {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 1.75rem;
  cursor: pointer;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

.faq-question h3 {
  font-size: 1.1rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
  flex: 1;
  padding-right: 1rem;
}

.faq-icon {
  font-size: 1.5rem;
  font-weight: bold;
  color: #6366f1;
  transition: transform 0.2s ease;
}

.faq-icon.active {
  transform: rotate(45deg);
}

.faq-answer {
  padding: 0 1.75rem 1.5rem;
  background: #fff;
}

.faq-answer p {
  margin: 0;
  color: #4b5563;
  line-height: 1.6;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}

@media (max-width: 640px) {
  h1 {
    font-size: 2rem;
  }

  .section {
    padding: 1.5rem;
  }

  .paste-link-card {
    border-radius: 1rem;
    padding: 1rem;
  }

  .faq-section {
    padding: 60px 0;
  }

  .faq-question {
    padding: 1.25rem;
  }

  .faq-answer {
    padding: 0 1.25rem 1.25rem;
  }
}
</style>


